import { useState, useEffect, useRef } from 'react'
import { sendChatMessage, DEFAULT_PROMPTS, type Message } from '../lib/chatApi'
import OrangeOrb3D from '../components/OrangeOrb3D'

export default function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [conversationId, setConversationId] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [showFileMenu, setShowFileMenu] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<any>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const finalTranscriptRef = useRef('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Cleanup voice recording on unmount
  useEffect(() => {
    return () => {
      try {
        if (recognitionRef.current) {
          recognitionRef.current.stop()
          recognitionRef.current = null
        }
      } catch (e) {
        // ignore
      }
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop())
          streamRef.current = null
        }
      } catch (e) {
        // ignore
      }
    }
  }, [])

  const handleSendMessage = async (text: string) => {
    const cleanText = text.trim()
    if (!cleanText || loading) return

    // Add user message to chat
    const userMessage: Message = { role: 'user', content: cleanText }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setInput('')
    setLoading(true)

    // Send to API
    const result = await sendChatMessage(cleanText, updatedMessages, conversationId, [])

    if (result.success) {
      const assistantMessage: Message = { role: 'assistant', content: result.response }
      setMessages((prev) => [...prev, assistantMessage])
      
      // Update conversation ID if this is a new conversation
      if (result.conversationId && result.conversationId !== conversationId) {
        setConversationId(result.conversationId)
      }
    } else {
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Something went wrong. Please try again.',
      }
      setMessages((prev) => [...prev, errorMessage])
    }

    setLoading(false)
  }

  const handlePromptClick = (prompt: typeof DEFAULT_PROMPTS[0], index: number) => {
    setSelectedPromptIndex(index)
    handleSendMessage(prompt.content)
    setTimeout(() => setSelectedPromptIndex(null), 300)
  }

  const handleNewChat = () => {
    setConversationId('')
    setMessages([])
    setInput('')
  }

  const startVoiceRecording = async () => {
    try {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      if (!SpeechRecognition) {
        alert('Voice input is not supported in your browser. Please use Chrome, Edge, or Safari.')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Web Speech API
      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'
      recognition.maxAlternatives = 1

      recognition.onstart = () => {
        setIsRecording(true)
        finalTranscriptRef.current = ''
      }

      recognition.onresult = (event: any) => {
        let interimTranscript = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            finalTranscriptRef.current += transcript + ' '
          } else {
            interimTranscript += transcript
          }
        }
        const fullText = (finalTranscriptRef.current + interimTranscript).trim()
        setInput(fullText)
      }

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error)
        if (event?.error && !['no-speech', 'aborted'].includes(event.error)) {
          alert(`Voice recognition error: ${event.error}`)
        }
      }

      recognitionRef.current = recognition
      recognition.start()

      // MediaRecorder for Whisper backup
      let mediaRecorder
      try {
        mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      } catch (e) {
        mediaRecorder = new MediaRecorder(stream)
      }

      audioChunksRef.current = []
      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data)
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(1000)
    } catch (error: any) {
      console.error('Error starting voice recording:', error)
      if (error?.name === 'NotAllowedError') {
        alert('Microphone access denied. Please allow microphone access in your browser settings.')
      } else {
        alert(`Error starting voice recording: ${error?.message || 'Unknown error'}`)
      }
      setIsRecording(false)
    }
  }

  const stopVoiceRecording = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionRef.current = null
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
    } catch (error) {
      console.error('Error stopping voice recording:', error)
    } finally {
      setIsRecording(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    
    // Handle file upload - for now just show in console
    console.log('Files selected:', files)
    alert('File upload feature coming soon!')
    setShowFileMenu(false)
  }

  return (
    <div className="flex flex-col h-screen max-w-4xl mx-auto">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {messages.length === 0 ? (
          /* Welcome Screen */
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center max-w-2xl w-full">
              {/* Orange Orb */}
              <div className="mb-6 flex justify-center">
                <div className="w-24 h-24">
                  <OrangeOrb3D size={96} />
                </div>
              </div>
              
              <h1 className="text-3xl font-semibold mb-3 text-gray-900 dark:text-white">
                How can I help you today?
              </h1>
              
              {/* Prompt Grid */}
              <div className="grid grid-cols-2 gap-3 mt-8 max-w-2xl mx-auto">
                {DEFAULT_PROMPTS.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt, index)}
                    disabled={loading}
                    className={`
                      group p-4 text-left rounded-2xl border transition-all
                      ${
                        selectedPromptIndex === index
                          ? 'border-[#FF2300] bg-orange-50 dark:bg-orange-950/20 scale-[0.98]'
                          : 'border-gray-200 dark:border-gray-700 hover:border-[#FF2300] hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }
                      ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">
                      {prompt.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Messages List */
          <div className="space-y-6 max-w-3xl mx-auto">
            {/* New Chat Button - Floating */}
            <div className="flex justify-end mb-4">
              <button
                onClick={handleNewChat}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                + New chat
              </button>
            </div>

            {messages.map((message, index) => (
              <div key={index} className="flex gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-600 dark:to-gray-800 text-white text-sm font-semibold">
                  {message.role === 'user' ? 'Y' : (
                    <div className="w-6 h-6 bg-gradient-to-br from-[#FF2300] to-orange-600 rounded-full" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 space-y-2">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white">
                    {message.role === 'user' ? 'You' : 'Dizee AI'}
                  </div>
                  <div className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </div>
                </div>
              </div>
            ))}

            {/* Loading Indicator */}
            {loading && (
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#FF2300] to-orange-600" />
                <div className="flex-1 space-y-2">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white">Dizee AI</div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="relative flex items-end gap-2 p-2 rounded-3xl border border-gray-200 dark:border-gray-700 focus-within:border-[#FF2300] transition-colors shadow-sm" style={{ backgroundColor: 'rgb(var(--surface-rgb))' }}>
            {/* Plus Button - File Upload */}
            <div className="relative">
              <button
                onClick={() => setShowFileMenu(!showFileMenu)}
                disabled={loading}
                className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach files"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              
              {showFileMenu && (
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-10">
                  <button
                    onClick={() => {
                      fileInputRef.current?.click()
                      setShowFileMenu(false)
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload file
                  </button>
                  <button
                    onClick={() => {
                      alert('Download templates feature coming soon!')
                      setShowFileMenu(false)
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download template
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept=".csv,.xlsx,.xls,image/*,.pdf"
              />
            </div>

            {/* Text Input */}
            <div className="flex-1 min-w-0">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage(input)
                  }
                }}
                placeholder="Message Dizee AI..."
                disabled={loading}
                rows={1}
                className="w-full px-1 py-2 bg-transparent border-none focus:outline-none resize-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:cursor-not-allowed"
                style={{ maxHeight: '200px' }}
              />
            </div>

            {/* Voice Button */}
            {!input.trim() && (
              <button
                onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                disabled={loading}
                className={`p-2.5 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
                title={isRecording ? 'Stop recording' : 'Voice input'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isRecording ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  )}
                </svg>
              </button>
            )}

            {/* Send Button */}
            {input.trim() && (
              <button
                onClick={() => handleSendMessage(input)}
                disabled={!input.trim() || loading}
                className="p-2.5 rounded-xl bg-[#FF2300] hover:bg-[#E61F00] text-white transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                title="Send message"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            )}
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
            Dizee AI can make mistakes. Check important info.
          </p>
        </div>
      </div>
    </div>
  )
}
